import type {
  OutstandingAction,
  OutstandingDocType,
  OutstandingImportDTO,
  OutstandingKind,
  OutstandingRowDTO,
  OutstandingRowStatus,
} from "../data/outstandingApi";

// How the four outcomes of the diff are named and coloured on screen, plus the
// words that differ between the customer sheet and the vendor one.

/**
 * Problems first, then work, then the rows that cost nothing.
 *
 * `unchanged` is last but never hidden: "125 unchanged" is the evidence that
 * the other 125 lines were looked at rather than left out of the file, and a CA
 * checking that their edit to row 61 registered needs somewhere to look.
 */
export const ACTION_ORDER: OutstandingAction[] = ["invalid", "new", "update", "unchanged"];

export const ACTION_LABEL: Record<OutstandingAction, string> = {
  invalid: "Needs fixing",
  new: "To be added",
  update: "To be changed",
  unchanged: "Unchanged",
};

/**
 * The same groups once the push has run — outcome tense, not intent tense.
 * "To be added: 8" over eight rows all badged "Added" reads as a queue that
 * never drained. The rows stay grouped by sheet action (see the rows
 * component's docblock for why); only the heading changes tense.
 */
export const PUSHED_ACTION_LABEL: Record<OutstandingAction, string> = {
  invalid: "Not sent — needs fixing",
  new: "Added",
  update: "Changed",
  unchanged: "Unchanged — not sent",
};

export const ACTION_STYLE: Record<OutstandingAction, string> = {
  invalid: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  new: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  update: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
  unchanged: "bg-muted text-muted-foreground",
};

/** The column headings a diff can name, in the order the sheet has them. */
const FIELD_LABEL: Record<string, string> = {
  contact_name: "Name",
  document_number: "Number",
  document_date: "Document Date",
  due_date: "Due Date",
  amount: "Amount",
  exchange_rate: "Exchange Rate",
  currency_code: "Currency",
  doc_type: "Type",
};

export function fieldLabel(field: string): string {
  return FIELD_LABEL[field] ?? field;
}

/** An empty cell reads better as a dash than as nothing in a before/after. */
export function cellText(value: unknown): string {
  return value === null || value === undefined || value === "" ? "-" : String(value);
}

export function groupByAction(
  rows: OutstandingRowDTO[]
): { action: OutstandingAction; rows: OutstandingRowDTO[] }[] {
  return ACTION_ORDER.map((action) => ({
    action,
    rows: rows.filter((row) => row.action === action),
  })).filter((group) => group.rows.length > 0);
}

/**
 * Warnings on rows that are otherwise fine.
 *
 * Counted separately from `invalid_rows` because they are a different question.
 * An invalid row will not be sent; a warned row will be, and the warning is the
 * last chance to say don't.
 */
export function warningCount(rows: OutstandingRowDTO[]): number {
  return rows.filter(
    (row) => row.action !== "invalid" && row.issues.some((issue) => issue.severity === "warning")
  ).length;
}

// ── After the push ──────────────────────────────────────────────────────────

/**
 * What happened to a row, in the past tense.
 *
 * Only shown once a push has run. Before that every row is `pending`, and
 * labelling a hundred rows "Not sent" would read as a problem rather than as
 * "nothing has happened yet".
 */
export const ROW_STATUS_LABEL: Record<OutstandingRowStatus, string> = {
  pending: "Not sent",
  created: "Added",
  updated: "Changed",
  skipped: "Nothing to do",
  failed: "Zoho refused",
  // Not "refused". Zoho declining to make a second document with a number it
  // already holds is the migration's own goal already met, and calling it a
  // failure sent CAs looking for something to fix that was not broken.
  duplicate: "Already there",
};

export const ROW_STATUS_STYLE: Record<OutstandingRowStatus, string> = {
  pending: "bg-muted text-muted-foreground",
  created: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  updated: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
  skipped: "bg-muted text-muted-foreground",
  failed: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  // Neutral, not amber: nothing here needs doing.
  duplicate: "bg-muted text-muted-foreground",
};

/**
 * Has this import been sent anywhere yet?
 *
 * The question the screen turns on: before, it shows what WOULD change and
 * offers a push; after, it shows what DID and offers no such thing. `status`
 * rather than a count, because a push that failed every row has still been run
 * and must not offer itself again as though it had not.
 */
export function hasBeenPushed(summary: OutstandingImportDTO): boolean {
  return summary.status === "committing" || summary.status === "done";
}

// ── The words that differ between the two sheets ────────────────────────────

/**
 * One map for both, because a chip on the customer sheet can still need to
 * name a credit shape and vice versa — the vocabulary is shared even when the
 * sheet is not.
 */
const DOC_TYPE_LABEL: Record<OutstandingDocType, string> = {
  invoice: "Invoice",
  credit_note: "Credit note",
  bill: "Bill",
  vendor_credit: "Vendor credit",
};

/**
 * Everything the two panel instances say differently, in one place.
 *
 * The panels themselves are one component rendered twice — kind "invoice" is
 * the customer sheet, kind "bill" the vendor one — and every word that varies
 * between them lives here rather than in scattered ternaries.
 */
export const KIND_COPY: Record<
  OutstandingKind,
  {
    title: string;
    description: string;
    contactWord: string;
    docTypeLabel: Record<OutstandingDocType, string>;
    noun: string;
    nounPlural: string;
  }
> = {
  invoice: {
    title: "Outstanding customer invoices",
    description:
      "Every invoice unpaid as of the account start date, plus a credit-note row for any customer holding an advance.",
    contactWord: "customer",
    docTypeLabel: DOC_TYPE_LABEL,
    noun: "document",
    nounPlural: "documents",
  },
  bill: {
    title: "Outstanding vendor bills",
    description:
      "Every bill unpaid as of the account start date, plus a vendor-credit row for any vendor holding an advance.",
    contactWord: "vendor",
    docTypeLabel: DOC_TYPE_LABEL,
    noun: "document",
    nounPlural: "documents",
  },
};

/**
 * Indian grouping, two decimals, no symbol — the house reading of a money
 * figure. The symbol is left off because an FX row prefixes its own currency
 * code, and a ₹ in front of a KWD amount would be a lie.
 */
export function formatMoney(value: number): string {
  return value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
